import { normalizeDomain } from "../domain.utils";

/**
 * List-membership helpers for the four per-site blacklists, mirroring the
 * extension's readers/writers (flymsg-extension src/shared/utils/siteDisabled.ts:
 * isDomainInList / addDomainToList / removeDomainFromList). Both sides of every
 * comparison go through `normalizeDomain`, so legacy full-URL entries
 * ("https://www.linkedin.com/feed"), "www."-prefixed hosts and mixed case all
 * match the bare host the extension stores today.
 */

/** True when `domain` matches any entry of `list`, compared normalized. */
export function isDomainInList(domain: string, list: string[] | undefined): boolean {
  const host = normalizeDomain(domain);
  if (!list || list.length === 0 || host === null) return false;
  return list.some((entry) => normalizeDomain(entry) === host);
}

/**
 * Add `domain` unless an equivalent entry is already present. Always stores the
 * normalized bare host, so app writes converge on the extension's format.
 */
export function addDomainToList(list: string[] | undefined, domain: string): string[] {
  const next = [...(list ?? [])];
  const host = normalizeDomain(domain);
  if (host === null || isDomainInList(host, next)) return next;
  next.push(host);
  return next;
}

/** Remove every entry matching `domain`, in whatever form it was stored. */
export function removeDomainFromList(list: string[] | undefined, domain: string): string[] {
  if (!list || list.length === 0) return [];
  const host = normalizeDomain(domain);
  if (host === null) return [...list];
  return list.filter((entry) => normalizeDomain(entry) !== host);
}

/**
 * The platform whose "Plugin" (compose & toolbar buttons) exists on `domain`,
 * or null when the plugin does not apply there. Domain-shaped mirror of the
 * extension popup's `detectPlatform` (flymsg-extension src/popup/platform.ts),
 * which is what decides where the popup shows its Plugin toggle.
 */
export function platformPluginLabel(domain: string): string | null {
  const host = normalizeDomain(domain);
  if (host === null) return null;
  if (host === "mail.google.com") return "Gmail";
  if (/^([a-z0-9-]+\.)*linkedin\.com$/.test(host)) return "LinkedIn";
  if (
    /^(outlook\.(live|office|office365)\.com|outlook\.cloud\.microsoft|m365\.cloud\.microsoft|([a-z0-9-]+\.)*mcas\.ms)$/.test(
      host
    )
  ) {
    return "Outlook";
  }
  return null;
}
